import { EntityType, Languages, NotificationChannel } from '@frontend/common';
import { LanguageSelect, MultiSelectInputV2, NumberInput, TextInput } from '@frontend/components/basic-forms';
import { AsyncComponent } from '@frontend/components/common';
import { DocumentSelect } from '@frontend/document/select';
import { EquipmentSelectV2 } from '@frontend/equipment/select';
import { EquipmentType } from '@frontend/equipment/types';
import { GroupSelectV2 } from '@frontend/group/select';
import { NodeType } from '@frontend/group/types';
import { IoTSelectV2 } from '@frontend/iot/select';
import { LabelTemplateSelect } from '@frontend/label/select';
import { PlaylistSelectV2 } from '@frontend/playlist/select';
import { CustomRoleSelect } from '@frontend/role/select';
import { ScreenTypeSelect, WizardSelect } from '@frontend/wizard/select';
import { WorkflowSelectV2 } from '@frontend/workflow/select';
import { WorkflowType } from '@frontend/workflow/types';
import { lazy, useMemo } from 'react';

import { BoolField } from './input-fields/bool-field/bool-field';
import { ColliWorkflowField } from './input-fields/colli-workflow-field/colli-workflow-field';
import { EnumField } from './input-fields/enum-field/enum-field';
import { PackageWorkflowField } from './input-fields/package-workflow-field/package-workflow-field';
import { SlotField } from './input-fields/slot-field/slot-field';
import { SpotField } from './input-fields/spot-field/spot-field';
import { TransactionStateField } from './input-fields/transaction-state-field/transaction-state-field';
import { TransactionStatesField } from './input-fields/transaction-states-field/transaction-states-field';
import { TransactionWorkflowField } from './input-fields/transaction-workflow-field/transaction-workflow-field';
import { SettingFieldInputProps } from './setting-field-input.component';

const UserSelectV2 = lazy(() => import('@frontend/user/select').then((module) => ({ default: module.UserSelectV2 })));

const useSettingFieldInput = (props: SettingFieldInputProps) => {
    const input = useMemo(() => {
        switch (props.type) {
            case 'str':
                return (
                    <TextInput
                        {...props}
                        value={props.value as string}
                    />
                );
            case 'int':
            case 'float':
                return (
                    <NumberInput
                        {...props}
                        step={props.type === 'int' ? 1 : undefined}
                        value={props.value as number | string | undefined}
                        onChange={(value) => props.onChange(value)}
                    />
                );
            case 'bool':
                return (
                    <BoolField
                        {...props}
                        id={props.type + '-' + props.label}
                        value={props.value as unknown as boolean}
                    />
                );
            case 'channels':
                return (
                    <MultiSelectInputV2
                        {...props}
                        onSelect={(value) => {
                            if (value === null) return;
                            props.onChange(value.map((v) => v.value));
                        }}
                        value={props.value ? (props.value as NotificationChannel[]) : []}
                        options={Object.entries(NotificationChannel).map((l) => ({ value: l[1], label: l[1] }))}
                    />
                );
            case 'document_id':
                return (
                    <DocumentSelect
                        {...props}
                        value={props.value as string | null}
                        queryParams={{ entity_type: props.targetEntityType, entity_id: props.targetEntityId }}
                    />
                );
            case 'playlist_id':
                return (
                    <PlaylistSelectV2
                        {...props}
                        value={props.value as string | null}
                    />
                );

            case 'product_root_category_id':
                return (
                    <GroupSelectV2
                        {...props}
                        value={props.value as string | null}
                        type={EntityType.PRODUCT_CATEGORY}
                        queryParams={{ node_type: NodeType.ROOT }}
                    />
                );
            case 'languages':
                return (
                    <MultiSelectInputV2
                        {...props}
                        onSelect={(value) => {
                            if (value === null) return;
                            props.onChange(value.map((v) => v.value));
                        }}
                        value={props.value ? (props.value as string[]) : []}
                        options={Languages.map((l) => ({ value: l.value, label: l.displayName }))}
                    />
                );
            case 'language':
                return (
                    <LanguageSelect
                        {...props}
                        onSelect={(value) => {
                            if (value === null) return;
                            props.onChange(value.value);
                        }}
                        value={props.value as string | null}
                    />
                );
            case 'label_template_id':
                return (
                    <LabelTemplateSelect
                        {...props}
                        value={props.value as string | null}
                    />
                );
            case 'transaction_state':
                return (
                    <TransactionStateField
                        {...props}
                        value={props.value as string | null}
                    />
                );
            case 'transaction_states':
                return (
                    <TransactionStatesField
                        {...props}
                        value={props.value ? (props.value as string[]) : []}
                    />
                );
            case 'transaction_workflow_id':
                return (
                    <TransactionWorkflowField
                        {...props}
                        value={props.value as string | null}
                    />
                );
            case 'colli_workflow_id':
                return (
                    <ColliWorkflowField
                        {...props}
                        value={props.value as string | null}
                    />
                );
            case 'package_workflow_id':
                return (
                    <PackageWorkflowField
                        {...props}
                        value={props.value as string | null}
                    />
                );
            case 'order_workflow_id':
                return (
                    <WorkflowSelectV2
                        {...props}
                        queryParams={{ type: WorkflowType.ORDER }}
                        value={props.value as string | null}
                        isClearable
                    />
                );
            case 'equipment_workflow_id':
                return (
                    <WorkflowSelectV2
                        {...props}
                        queryParams={{ type: WorkflowType.EQUIPMENT }}
                        value={props.value as string | null}
                        isClearable
                    />
                );
            case 'user_workflow_id':
                return (
                    <WorkflowSelectV2
                        {...props}
                        queryParams={{ type: WorkflowType.USER }}
                        value={props.value as string | null}
                        isClearable
                    />
                );
            case 'deceased_workflow_id':
                return (
                    <WorkflowSelectV2
                        {...props}
                        queryParams={{ type: WorkflowType.DECEASED }}
                        value={props.value as string | null}
                        isClearable
                    />
                );
            case 'equiment_container_id':
                return (
                    <EquipmentSelectV2
                        queryParams={{ type: EquipmentType.CONTAINER }}
                        {...props}
                        value={props.value as string | null}></EquipmentSelectV2>
                );
            case 'custom_role_id':
                return (
                    <CustomRoleSelect
                        {...props}
                        queryParams={{ entity_type: props.targetEntityType }}
                        value={props.value as string | null}
                    />
                );
            case 'user_group_id':
                return (
                    <GroupSelectV2
                        {...props}
                        type={EntityType.USER_GROUP}
                        value={props.value as string | null}
                    />
                );
            case 'user_id':
                return (
                    <AsyncComponent>
                        <UserSelectV2
                            {...props}
                            value={props.value as string | null}
                        />
                    </AsyncComponent>
                );
            case 'sso_provider':
                return (
                    <EnumField
                        {...props}
                        options={['microsoft', 'google', 'itsme']}
                        value={props.value as string | null}
                    />
                );
            case 'spot_id':
                return (
                    <SpotField
                        {...props}
                        value={props.value as string | null}
                    />
                );
            case 'slot_id':
                return (
                    <SlotField
                        {...props}
                        value={props.value as string | null}
                    />
                );
            case 'wizard_id':
                return (
                    <WizardSelect
                        {...props}
                        value={props.value as string | null}
                    />
                );
            case 'printer_id':
            case 'payment_terminal_id':
                return (
                    <IoTSelectV2
                        {...props}
                        queryParams={{ type: props.type.slice(0, -3) as string }}
                        value={props.value as string | null}
                    />
                );
            case 'enum':
                return (
                    <EnumField
                        {...props}
                        value={props.value as string | null}
                    />
                );
            case 'screen_type':
                return (
                    <ScreenTypeSelect
                        {...props}
                        onSelect={(v) => props.onChange(v?.value || null)}
                        value={props.value as string | null}
                    />
                );
            default:
                return <div>Unsupported field type: {props.type}</div>;
        }
    }, [props.type, props.value, props.templateKey]);

    return {
        input
    };
};

export default useSettingFieldInput;
